import { createContext, useContext, useEffect, useState } from "react";
import api from "../services/api";
import { toast } from "react-toastify";
import { unregisterWebPushSubscription } from "../services/webPush";

const AuthContext = createContext({})

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState({})
    const [permissions, setPermissions] = useState({})
    const [modules, setModules] = useState({})
    const [statusAccount, setStatusAccount] = useState({ hasPermission: false, payment_by_partner: false })
    const [account, setAccount] = useState({})
    const [token, setToken] = useState()
    const [logged, setLogged] = useState(false)
    const [showAllOptions, setShowAllOptions] = useState(true)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        verifyUserLogged(true)
    }, [])

    useEffect(() => {
        const handleAuthStorageChange = (event) => {
            if (event.key !== "@oktto:token" && event.key !== "@oktto:user") {
                return
            }

            const storagedUser = localStorage.getItem("@oktto:user")
            const storagedToken = localStorage.getItem("@oktto:token")

            if (storagedUser && storagedToken) {
                verifyUserLogged(false, null, { silent: true })
            } else {
                clearUserState()
            }
        }

        window.addEventListener("storage", handleAuthStorageChange)
        return () => window.removeEventListener("storage", handleAuthStorageChange)
    }, [])

    useEffect(() => {
        const isRemoteAccess = permissions?.accessByAdmin || permissions?.accessByPartner
        if (!isRemoteAccess && !permissions?.is_admin && !statusAccount?.hasPermission && permissions.is_partner) {
            setShowAllOptions(false)
        } else {
            setShowAllOptions(true)
        }
    }, [permissions, statusAccount])

    const clearUserState = () => {
        setUser({})
        setToken()
        api.defaults.headers.common['Authorization'] = undefined
        setLogged(false)
    }

    const setUserAndToken = (token, user) => {
        if (token && user) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`
            localStorage.setItem("@oktto:token", token)
            localStorage.setItem("@oktto:user", JSON.stringify(user))
            setToken(token)
            setUser(user)
            setLogged(true)
        } else {
            localStorage.removeItem("@oktto:token")
            localStorage.removeItem("@oktto:user")
            clearUserState()
        }

    }

    async function login(data) {
        try {
            if (!data?.email) {
                toast.error("O e-mail é obrigatório!")
                return false
            }
            if (!data?.password) {
                toast.error("A senha é obrigatória!")
                return false
            }

            setLoading(true)
            const response = await api.post("/users/login", { "username": data.email, "password": data.password })
            const tokenUser = response?.data?.data?.token
            const user = response?.data?.data?.user

            if (!tokenUser || !user) {
                setUserAndToken()
                toast.error("Não foi possível concluir o login. Tente novamente!")
                return false
            }

            setUserAndToken(tokenUser, user)
            toast.success(response.data.message)
            return true
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível concluir o login. Tente novamente!")
            return false
        } finally {
            setLoading(false)
        }
    }

    async function finishWelcome(data, token) {
        try {
            if (!data?.name?.trim()) {
                toast.error("O nome é obrigatório!")
                return false
            }
            if (!data?.password || !data?.confirmPassword) {
                toast.error("A senha e a confirmação de senha são obrigatórias!")
                return false
            }
            if (data?.password?.length < 8) {
                toast.error("A senha deve ter pelo menos 8 caracteres!")
                return false
            }
            if (data?.password !== data?.confirmPassword) {
                toast.error("A senha e a confirmação de senha devem ser iguais!")
                return false
            }
            setLoading(true)
            const response = await api.post(`/users/validate-user/${token}`, data)
            const tokenUser = response?.data?.data?.token
            const user = response?.data?.data?.user
            setUserAndToken(tokenUser, user)
            setLoading(false)
            toast.success(response?.data?.message)
            return true
        } catch (err) {
            setLoading(false)
            toast.error(err?.response?.data?.message)
            return false
        }
    }

    async function resetPassword(data, token) {
        try {
            if (!token) {
                if (data?.email) {
                    setLoading(true)
                    const response = await api.post("/users/reset-password", { email: data.email })
                    setLoading(false)
                    toast.success(response?.data?.message)
                }
            } else {
                if (!data?.email) {
                    toast.error("O e-mail é obrigatório!")
                    return false
                }
                if (!data?.password || !data?.confirmPassword) {
                    toast.error("A senha e a confirmação de senha são obrigatórias!")
                    return false
                }
                if (data?.password !== data?.confirmPassword) {
                    toast.error("A senha e a confirmação de senha devem ser iguais!")
                    return false
                }

                setLoading(true)
                const response = await api.post(`/users/reset-password/${token}`, data)
                const tokenUser = response?.data?.data?.token
                const user = response?.data?.data?.user
                setUserAndToken(tokenUser, user)
                setLoading(false)
                toast.success(response?.data?.message)
            }
            return true
        } catch (err) {
            setLoading(false)
            toast.error(err?.response?.data?.message)
            return false
        }
    }

    async function handleLogout(options = {}) {
        const currentToken = localStorage.getItem("@oktto:token")
        if (options?.expectedToken && currentToken !== options.expectedToken) {
            return false
        }

        setLoading(true)
        const unregisterSubscription = unregisterWebPushSubscription().catch(() => { })
        setUserAndToken()
        await unregisterSubscription
        setLoading(false)
        return true
    }

    function verifyUserLogged(shouldLogout = false, logoutMessage = null, options = {}) {
        const shouldUpdateLoading = !options?.silent
        if (shouldUpdateLoading) {
            setLoading(true)
        }
        const storagedUser = localStorage.getItem("@oktto:user")
        const storagedToken = localStorage.getItem("@oktto:token")
        if (storagedUser && storagedToken) {
            api.defaults.headers.common['Authorization'] = `Bearer ${storagedToken}`
            api.get("/users/verify", {
                headers: { Authorization: `Bearer ${storagedToken}` }
            }).then(response => {
                if (localStorage.getItem("@oktto:token") !== storagedToken) {
                    return
                }

                setToken(storagedToken)
                setUser(response?.data?.data || JSON.parse(storagedUser))
                setLogged(true)
            }).catch(err => {
                const status = err?.response?.status
                const isCurrentSession = localStorage.getItem("@oktto:token") === storagedToken
                if (status === 401 && shouldLogout && isCurrentSession) {
                    handleLogout({ expectedToken: storagedToken })
                    toast.error(logoutMessage || err?.response?.data?.message || "Sua sessão expirou!")
                }
            }).finally(() => {
                if (shouldUpdateLoading) {
                    setLoading(false)
                }
            })
        } else {
            if (shouldUpdateLoading) {
                setLoading(false)
            }
        }
    }

    async function getPermissions(account_id, { snapshot = false } = {}) {
        try {
            const response = await api.get("/users/permissions", { headers: { "x-api-key": account_id }, timeout: 15000 })
            const newPermissions = response?.data?.data?.permissions
            const newModules = response?.data?.data?.modules
            const newStatusAccount = response?.data?.data?.status_account

            if (!newPermissions || typeof newPermissions !== "object" || Array.isArray(newPermissions)
                || (account_id && typeof newStatusAccount?.hasPermission !== "boolean")) {
                throw new Error("Não foi possível carregar as permissões.")
            }

            if (JSON.stringify(newPermissions) !== JSON.stringify(permissions)) {
                setPermissions(newPermissions)
            }
            if (JSON.stringify(newModules) !== JSON.stringify(modules)) {
                setModules(newModules)
            }
            if (JSON.stringify(newStatusAccount) !== JSON.stringify(statusAccount)) {
                setStatusAccount(newStatusAccount)
            }

            return snapshot ? { permissions: newPermissions, statusAccount: newStatusAccount } : newPermissions
        } catch (err) {
            if (snapshot) throw err
            toast.error(err?.response?.data?.message || "Não foi possível carregar as permissões. Tente novamente.")
        }
    }

    async function verifyIfUserHasPermission(namePermission, account_id) {
        const permissions = await getPermissions(account_id)
        let hasPermission = permissions && permissions[namePermission] >= 1
        return hasPermission
    }

    async function getAccount(account_id) {
        try {
            const response = await api.get(`/accounts/${account_id}`, { timeout: 15000 })
            const account = response?.data?.data
            setAccount(account)
            return account
        } catch (err) {
            toast.error(err?.response?.data?.message)
        }
    }

    function verifyIfModuleIsActive(action) {
        return modules?.length > 0 && modules.some(module => module.action === action);
    }

    function updateNameUser(name) {
        setUser(previousValue => { return { ...previousValue, name } })
    }

    function updateEmailUser(email) {
        setUser(previousValue => { return { ...previousValue, email } })
    }

    function updateImageUser(image) {
        if (image && user?.image) {
            setUser(previousValue => { return { ...previousValue, image: false } })
        }
        setTimeout(() => {
            setUser(previousValue => { return { ...previousValue, image } })
        }, 100)
    }

    return (
        <AuthContext.Provider value={{ loading, logged, user, updateNameUser, updateEmailUser, updateImageUser, token: token ? btoa(token) : undefined, permissions, getPermissions, verifyIfUserHasPermission, showAllOptions, modules, statusAccount, account, getAccount, verifyIfModuleIsActive, login, handleLogout, verifyUserLogged, finishWelcome, resetPassword }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    return context
}

export default AuthContext
